'use client';
import React, { startTransition, useEffect } from 'react';
import { DateTime } from 'luxon';

import Layout from '../lib/components/Layout';
import { ISetParams, Ratings } from '@benzinga/calendar-manager';
import { SessionContext } from '@benzinga/session-context';
import ColumnsDefinition from './ColumnsDef';
// import { formatRequestDate } from '../lib/utils';
import { formatRequestDate, sortAnalystRatings } from '../lib/utils';

import { fetchAnalystRatings } from './Services';
import { CALENDAR_FETCH_PAGE_SIZE, useCalendarPagedFetch } from '../lib/useCalendarPagedFetch';
import { ICalendarProps, IDateRange } from '../lib/interfaces';
import ColumnsDefinitionV2 from './ColumnsDefV2';

export const AnalystRatingsColumnsDef = ColumnsDefinition;
export const AnalystRatingsColumnsDefV2 = ColumnsDefinitionV2;
export const fetchAnalystRatingsData = fetchAnalystRatings;

const getTabDataByTabIndex = (index: number, dataTabs: [Ratings[], Ratings[], Ratings[], Ratings[]]) => {
  return dataTabs[index];
};

interface IAnalystRatingCalendarProps extends ICalendarProps {
  isV2?: boolean;
}

export const AnalystRatingCalendar: React.FC<IAnalystRatingCalendarProps> = ({
  additionalFetchParams,
  calendar,
  calendarData,
  calendarType,
  hasStockTableStyling,
  height,
  hiddenColumns,
  hideFilters,
  initialData,
  initialDateRange,
  initialTab,
  initialTicker,
  isRouteLoading,
  isV2 = false,
  leftSlot,
  onDataLoad,
  onReady,
  onTabChange: onPropsTabChange,
}) => {
  const session = React.useContext(SessionContext);
  const [allData, setAllData] = React.useState<Ratings[]>(initialData || []);
  const [upgradesData, setUpgradesData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.upgrades || [];
  });
  const [downgradesData, setDowngradesData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.downgrades || [];
  });
  const [initiationsData, setInitiationsData] = React.useState<Ratings[]>(() => {
    const ratings = sortAnalystRatings(allData);
    return ratings.initiations || [];
  });
  const [activeTabIndex, setActiveTabIndex] = React.useState(getTabIndex(initialTab));
  const dataTabs: [Ratings[], Ratings[], Ratings[], Ratings[]] = React.useMemo(
    () => [allData, upgradesData, downgradesData, initiationsData],
    [allData, downgradesData, initiationsData, upgradesData],
  );
  const [rowData, setRowData] = React.useState(() => {
    const tabData = getTabDataByTabIndex(activeTabIndex, dataTabs);
    return tabData || [];
  });
  let dateRange: IDateRange = {};

  if (initialDateRange) {
    dateRange = initialDateRange;
  } else {
    dateRange = {
      date_from: formatRequestDate(DateTime.now().minus({ months: 1 }).toJSDate()),
      date_to: formatRequestDate(new Date()),
    };
  }

  const setAnalystRatings = React.useCallback(
    (items: Ratings[]) => {
      const ratings = sortAnalystRatings(items);

      setAllData(items ?? []);
      setUpgradesData(ratings.upgrades);
      setDowngradesData(ratings.downgrades);
      setInitiationsData(ratings.initiations);
      onDataLoad && onDataLoad(items ?? []);
    },
    [onDataLoad],
  );

  const initialParams = React.useMemo(
    () =>
      ({
        dateFrom: dateRange.date_from,
        dateTo: dateRange.date_to,
        ...(initialTicker && { symbols: initialTicker }),
        ...(additionalFetchParams && additionalFetchParams),
      }) as ISetParams,
    [additionalFetchParams, dateRange.date_from, dateRange.date_to, initialTicker],
  );

  const {
    loading,
    onFilterChanged,
    onViewportChanged,
    setTableData: setPagedTableData,
    tableData: pagedTableData,
  } = useCalendarPagedFetch<Ratings, ISetParams>({
    fetchPage: (params, page) =>
      fetchAnalystRatingsData(session.getSession(), {
        ...(additionalFetchParams && additionalFetchParams),
        ...(params as ISetParams),
        page,
        pageSize: CALENDAR_FETCH_PAGE_SIZE,
      }),
    initialData,
    initialParams,
  });

  React.useEffect(() => {
    setAnalystRatings(pagedTableData);
  }, [pagedTableData, setAnalystRatings]);

  React.useEffect(() => {
    if (calendarData?.serverSide) {
      setPagedTableData(initialData ?? [], initialParams);
    }
  }, [calendarData?.serverSide, initialData, initialParams, setPagedTableData]);

  function onTableReady() {
    if (initialData) {
      setPagedTableData(initialData, initialParams);
    } else {
      onFilterChanged(initialParams);
    }

    if (onReady) {
      setTimeout(() => onReady());
    }
  }

  const fetchData = onFilterChanged;

  function tabs() {
    return [
      {
        hasParentUrl: true,
        key: 'all',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `All Ratings (${allData.length})`,
      },
      {
        key: 'upgrades',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Upgrades (${upgradesData.length})`,
      },
      {
        key: 'downgrades',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Downgrades (${downgradesData.length})`,
      },
      {
        key: 'initiations',
        link: 'https://www.benzinga.com/analyst-stock-ratings',
        title: `Initiations (${initiationsData.length})`,
      },
    ];
  }

  function onTabChange(index: number) {
    if (isRouteLoading) {
      return;
    }
    startTransition(() => {
      setActiveTabIndex(index);
      const data = getTabDataByTabIndex(index, dataTabs);
      if (data && data.length) {
        setTableData(data);
      }

      if (onPropsTabChange) {
        const targetTab = tabs()[index];
        if (targetTab) {
          onPropsTabChange(targetTab.hasParentUrl || targetTab.key);
        }
      }
    });
  }

  function setTableData(data: Ratings[]) {
    setRowData(data);
  }

  function getTabIndex(index: string | number | undefined | boolean): number {
    if (typeof index === 'number') {
      return index;
    } else if (typeof index === 'string') {
      const targetTabIndex = tabs().findIndex(tab => tab.key === index);

      return targetTabIndex > 0 ? targetTabIndex : 0;
    }

    return 0;
  }

  useEffect(() => {
    setTableData(getTabDataByTabIndex(activeTabIndex, dataTabs));
  }, [activeTabIndex, dataTabs]);

  const blurColumnsWhenGated = isV2
    ? new Set(['upside_downside', 'analyst_name', 'pt_prior', 'rating_current'])
    : undefined;

  return (
    <Layout
      blurColumnsWhenGated={blurColumnsWhenGated}
      calendar={calendar}
      calendarData={calendarData}
      calendarType={calendarType}
      columnDefs={isV2 ? ColumnsDefinitionV2 : ColumnsDefinition}
      enableSearch
      hasStockTableStyling={hasStockTableStyling}
      height={height}
      hiddenColumns={hiddenColumns}
      hideFilters={hideFilters}
      initialDateRange={dateRange}
      initialTabIndex={getTabIndex(initialTab)}
      isGated={true}
      isRouteLoading={isRouteLoading}
      leftSlot={leftSlot}
      onFilterChanged={fetchData}
      onTabChange={onTabChange}
      onTableReady={onTableReady}
      tableData={rowData}
      tableLoading={loading ?? isRouteLoading}
      tabs={tabs()}
      theme="modern"
      ticker={initialTicker}
      viewportChange={onViewportChanged}
    />
  );
};
